import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils';

import {
  ROUTES_TAG_TYPE,
  ALL_ROUTES_ID,
  ROUTES_PAGE_SIZE,
} from './constants';

// Request types
interface RouteParams {
  q?: string;
}

interface UpdateRouteRequest {
  routeId: number | string;
  label: string;
}

interface OptimizeRouteRequest {
  routeId: number | string;
  origin: { lat: number; lng: number };
  roundtrip: boolean;
}

interface RouteStop {
  provider_id: string;
  position: number;
  address_key?: string;
}

interface SetRouteStopsRequest {
  routeId: number | string;
  stops: RouteStop[];
}

interface CreateRouteRequest {
  label: string;
}

interface DeleteRouteStopRequest {
  routeId: number | string;
  stopId: number;
}

// Response types
export interface Route {
  id: number;
  label: string;
  owner?: number;
  origin_lat?: number | null;
  origin_lng?: number | null;
  stop_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface RouteStopResponse {
  id: number;
  route: number;
  name: string;
  lat: number;
  lng: number;
  provider_id: string;
  provider_type: string;
  address_key: string;
  position: number;
  created_at: string;
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  state: string;
  zip_code: string;
}

// API response wrapper for paginated endpoints
interface PaginatedResponse<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

export const routesApi = createApi({
  reducerPath: 'routesApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [ROUTES_TAG_TYPE],
  endpoints: (builder) => ({
    getAllRoutes: builder.query<Route[], RouteParams | void>({
      query: (params) => {
        const queryParams = buildSearchParamsAuto({
          ...params,
          pageSize: ROUTES_PAGE_SIZE,
        });
        const queryString = queryParams.toString();
        return `v1/routes/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: PaginatedResponse<Route> | Route[]) => {
        // Handle both paginated response and direct array response
        if (Array.isArray(response)) {
          return response;
        }
        return response.results || [];
      },
      providesTags: (result) => {
        const routes = result || [];
        return [
          { type: ROUTES_TAG_TYPE, id: ALL_ROUTES_ID },
          ...routes.map(({ id }) => ({ type: ROUTES_TAG_TYPE, id })),
        ];
      },
    }),
    getRouteDetails: builder.query<Route, number | string>({
      query: (routeId) => `v1/routes/${routeId}/`,
      providesTags: (result, error, routeId) => [
        { type: ROUTES_TAG_TYPE, id: routeId },
      ],
    }),
    getRouteStops: builder.query<RouteStopResponse[], number | string>({
      query: (routeId) => {
        const queryParams = buildSearchParamsAuto({
          pageSize: ROUTES_PAGE_SIZE,
        });
        return `v1/routes/${routeId}/stops/?${queryParams.toString()}`;
      },
      transformResponse: (response: PaginatedResponse<RouteStopResponse> | RouteStopResponse[]) => {
        // Handle both paginated response and direct array response
        if (Array.isArray(response)) {
          return response;
        }
        return response.results || [];
      },
      providesTags: (result, error, routeId) => [
        { type: ROUTES_TAG_TYPE, id: routeId },
      ],
    }),
    createRoute: builder.mutation<Route, CreateRouteRequest>({
      query: (newRoute) => ({
        url: 'v1/routes/',
        method: 'POST',
        body: newRoute,
      }),
      invalidatesTags: [{ type: ROUTES_TAG_TYPE, id: ALL_ROUTES_ID }],
    }),
    createSetRouteStops: builder.mutation<void, SetRouteStopsRequest>({
      query: ({ routeId, stops }) => ({
        url: `v1/routes/${routeId}/stops/`,
        method: 'POST',
        body: { stops },
      }),
      invalidatesTags: (result, error, { routeId }) => [
        { type: ROUTES_TAG_TYPE, id: routeId },
      ],
    }),
    updateRoute: builder.mutation<Route, UpdateRouteRequest>({
      query: ({ routeId, label }) => ({
        url: `v1/routes/${routeId}/`,
        method: 'PATCH',
        body: { label },
      }),
      invalidatesTags: (result, error, { routeId }) => [
        { type: ROUTES_TAG_TYPE, id: routeId },
        { type: ROUTES_TAG_TYPE, id: ALL_ROUTES_ID },
      ],
    }),
    optimizeRoute: builder.mutation<
      { stops: RouteStopResponse[] },
      OptimizeRouteRequest
    >({
      query: ({ routeId, origin, roundtrip }) => ({
        url: `v1/routes/${routeId}/optimize/`,
        method: 'POST',
        body: { origin, roundtrip },
      }),
      async onQueryStarted({ routeId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (Array.isArray(data?.stops)) {
            dispatch(
              routesApi.util.updateQueryData('getRouteStops', routeId, () => data.stops),
            );
          }
        } catch {
          // The caller handles the failed mutation via unwrap().
        }
      },
      invalidatesTags: (result, error, { routeId }) => [
        { type: ROUTES_TAG_TYPE, id: routeId },
        { type: ROUTES_TAG_TYPE, id: ALL_ROUTES_ID },
      ],
    }),
    deleteRoute: builder.mutation<void, number | string>({
      query: (routeId) => ({
        url: `v1/routes/${routeId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, routeId) => [
        { type: ROUTES_TAG_TYPE, id: routeId },
        { type: ROUTES_TAG_TYPE, id: ALL_ROUTES_ID },
      ],
    }),
    deleteRouteStop: builder.mutation<void, DeleteRouteStopRequest>({
      query: ({ routeId, stopId }) => ({
        url: `v1/routes/${routeId}/stops/${stopId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { routeId }) => [
        { type: ROUTES_TAG_TYPE, id: routeId },
      ],
    }),
  }),
});

export const {
  useGetAllRoutesQuery,
  useGetRouteDetailsQuery,
  useGetRouteStopsQuery,
  useCreateRouteMutation,
  useCreateSetRouteStopsMutation,
  useUpdateRouteMutation,
  useOptimizeRouteMutation,
  useDeleteRouteMutation,
  useDeleteRouteStopMutation,
} = routesApi;
